import { css } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { CSSProperties } from "react";

export const cssVariableMap = {
  variant: {
    outlined: {
      "--background-color": vars.semantic.color.field,
      "--outline-color": vars.semantic.color.border,
    } as CSSProperties,
    filled: {
      "--background-color": vars.semantic.color.fieldSub,
      "--outline-color": "transparent",
    } as CSSProperties,
    secondary: {
      "--background-color": vars.semantic.color.fieldSub,
      "--outline-color": "transparent",
    } as CSSProperties,
  },
  size: {
    medium: {
      "--padding-x": vars.spacing[3],
    } as CSSProperties,
    large: {
      "--padding-x": vars.spacing[4],
    } as CSSProperties,
  },
};

export const inputContainerCSS = css({
  padding: "0 var(--padding-x)",

  backgroundColor: "var(--background-color)",

  border: "none",
  borderRadius: vars.rounded.medium,

  outlineWidth: "1px",
  outlineStyle: "solid",
  outlineOffset: "-1px",
  outlineColor: "var(--outline-color)",

  "&:not(:where(:has([readonly]), [aria-invalid='true'], :has(input:focus))):hover": {
    outlineColor: vars.semantic.color.borderHover,
  },

  // disabled
  "&:where(:has(input:disabled))": {
    backgroundColor: vars.semantic.color.fieldDisabled,
    outline: "none",
    color: vars.semantic.color.textDisabled,
    cursor: "not-allowed",
  },

  // focus
  "&:where(:has(input:focus:not(:read-only)))": {
    outlineColor: vars.semantic.color.focus,
    backgroundColor: vars.semantic.color.surface,
    outlineWidth: "2px",
    outlineOffset: "-2px",
  },

  // invalid
  "&:where(:has([aria-invalid='true']:not(input:focus)))": {
    backgroundColor: vars.semantic.color.surfaceCriticalSecondary,
    outlineColor: "transparent",

    "&:hover": {
      outlineColor: vars.semantic.color.borderCriticalHover,
    },
  },

  // readonly
  "&:has([readonly])": {
    backgroundColor: vars.semantic.color.fieldSub,

    outline: "none",
    cursor: "default",
  },
});

export const inputCSS = css({
  flex: 1,
  width: "100%",

  padding: `${vars.spacing["25"]} 0`,

  '[data-clay-textfield-container-size="large"] &': {
    padding: `${vars.spacing["3"]} 0`,
    ...vars.semantic.typography["body-large"],

    "&::placeholder": {
      fontSize: vars.fontSize.small,
    },
  },

  margin: 0,
  outline: "none",
  border: "none",
  caretColor: vars.semantic.color.focus,

  textAlign: "var(--text-align)" as CSSProperties["textAlign"],
  ...vars.semantic.typography["body-medium"],
  color: vars.semantic.color.text,
  backgroundColor: "transparent",
  cursor: "inherit",

  "&::placeholder": {
    color: vars.semantic.color.textSub,
    lineHeight: 1,
  },

  "&:disabled": {
    color: vars.semantic.color.textDisabled,

    "&::placeholder": {
      color: "inherit",
    },
  },

  "&:not(:disabled):read-only": {
    color: vars.semantic.color.textSub,
  },

  "&[type='password']": {
    fontSize: "19.4px",
    letterSpacing: "2px",
    fontWeight: 900,

    "&::placeholder": {
      ...vars.semantic.typography["body-medium"],
      letterSpacing: "0",
    },
  },

  "&[type='password']:not(:placeholder-shown)": {
    fontSize: "19.4px",
    letterSpacing: "2px",
    fontWeight: 900,
  },

  "&:not([type='password']) ~ .clay-textfield-addon-password-button svg.clay-textfield-addon-password-hidden":
    {
      display: "none",
    },

  "&[type='password'] ~ .clay-textfield-addon-password-button svg.clay-textfield-addon-password-show":
    {
      display: "none",
    },

  "&::-webkit-search-cancel-button": {
    WebkitAppearance: "none",
    position: "relative",
    right: "-2px",

    width: "24px",
    height: "20px",

    background: `url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="var(--clay-color-semantic-icon-secondary)"><path d="M17 7L7 17M7 7L17 17" stroke="currentColor" strokeWidth="1.2" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" /></svg>'); no-repeat 50% 50%`,
    backgroundSize: "contain",
    backgroundPosition: "right",
  },

  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
});
